<script setup lang="ts">
import { computed } from 'vue'
import type { AnyCalculatorDefinition } from '@/types/calculator'
import { useCalculator } from '@/composables/useCalculator'
import { useFormatters } from '@/composables/useFormatters'
import { useCalculatorSeo } from '@/composables/useCalculatorSeo'
import { useShare } from '@/composables/useShare'
import { useAnalytics } from '@/composables/useAnalytics'
import { encodeInputs } from '@/calculators/shared/shareParams'
import { buildResultEmail, mailtoUrl, openMailto } from '@/calculators/shared/resultEmail'
import { useToastStore } from '@/stores/toast'
import { useAppStore } from '@/stores/app'
import PageLayout from '@/components/layout/PageLayout.vue'
import Alert from '@/components/ui/Alert.vue'
import Button from '@/components/ui/Button.vue'
import Card from '@/components/ui/Card.vue'
import DynamicField from '@/components/calculator/DynamicField.vue'
import ResultCard from '@/components/calculator/ResultCard.vue'
import ResultTable from '@/components/calculator/ResultTable.vue'
import AssumptionsPanel from '@/components/calculator/AssumptionsPanel.vue'
import CalculatorContent from '@/components/calculator/CalculatorContent.vue'
import RelatedCalculators from '@/components/calculator/RelatedCalculators.vue'
import Icon from '@/components/icons/Icon.vue'

const props = defineProps<{
  definition: AnyCalculatorDefinition
}>()

const { inputs, errors, result, hasCalculated, setField, reset, validate } = useCalculator(
  props.definition
)
const { formatCurrency } = useFormatters()
const { buildShareUrl, shareResult } = useShare()
const { track } = useAnalytics()
const toast = useToastStore()
const app = useAppStore()

function inputValue(name: string): string | number | boolean {
  const value = inputs.value[name]
  if (value === undefined || value === null) return ''
  return value as string | number | boolean
}

function shareUrl(): string {
  return buildShareUrl(
    `/${props.definition.slug}`,
    encodeInputs(props.definition.inputs, inputs.value)
  )
}

useCalculatorSeo(props.definition)

const resultCards = computed(() =>
  props.definition.resultSections.filter((s) => Boolean(s.highlighted))
)

const resultRows = computed(() =>
  props.definition.resultSections
    .filter((s) => !s.highlighted && result.value)
    .map((s: (typeof props.definition.resultSections)[number]) => ({
      label: s.label,
      value: s.value(result.value as Record<string, unknown>),
      format: s.format,
    }))
)

async function onShare() {
  if (!result.value) return
  const primary = resultCards.value[0]
  const primaryValue = primary ? primary.value(result.value as Record<string, unknown>) : ''
  const outcome = await shareResult({
    title: props.definition.title,
    text: `Result from ${props.definition.title}: ${typeof primaryValue === 'number' ? formatCurrency(primaryValue) : primaryValue}`,
    url: shareUrl(),
  })
  if (outcome === 'cancelled') return
  if (outcome === 'copied') {
    toast.show('Link copied to clipboard')
  } else if (outcome === 'shared') {
    toast.show('Result shared')
  } else {
    toast.show('Could not copy the link — please copy the address bar instead', 'error')
  }
  track({
    name: 'calculator_shared',
    calculatorId: props.definition.id,
    dataVersion: 'phase1-static',
  })
}

function onEmail() {
  if (!result.value) return
  const email = buildResultEmail({
    definition: props.definition,
    inputs: inputs.value,
    result: result.value,
    shareUrl: shareUrl(),
    siteName: app.siteName,
    currency: app.currency,
    taxYearLabel: `${Number(app.taxYear) - 1}/${app.taxYear}`,
  })
  openMailto(mailtoUrl(email))
  track({
    name: 'calculator_emailed',
    calculatorId: props.definition.id,
    dataVersion: 'phase1-static',
  })
}

function onSubmit() {
  validate()
  if (result.value) {
    track({
      name: 'calculator_completed',
      calculatorId: props.definition.id,
      dataVersion: 'phase1-static',
      resultSummaryBucket: 'shared',
    })
  }
}
</script>

<template>
  <PageLayout
    :title="definition.title"
    :description="definition.description"
    :breadcrumbs="[
      { label: 'Calculators', to: '/calculators' },
      { label: definition.shortTitle || definition.title },
    ]"
  >
    <div class="calculator">
      <section class="calculator__form" aria-labelledby="calculator-form-title">
        <h2 id="calculator-form-title" class="sr-only">Calculator inputs</h2>
        <Card accent="primary">
          <div class="calculator__form-header">
            <Icon name="calculator" :size="20" />
            <span>Enter your details</span>
          </div>
          <form class="calculator__fields" @submit.prevent="onSubmit">
            <DynamicField
              v-for="field in definition.inputs"
              :key="field.name"
              :field="field"
              :model-value="inputValue(field.name)"
              :error="errors[field.name]"
              @update:model-value="(value) => setField(field.name, value)"
            />
            <div class="calculator__actions">
              <Button type="submit" size="lg">
                <Icon name="calculator" :size="18" />
                Calculate
              </Button>
              <Button type="button" variant="secondary" @click="reset">
                <Icon name="reset" :size="16" />
                Reset
              </Button>
            </div>
          </form>
        </Card>
      </section>

      <section
        class="calculator__results"
        aria-labelledby="calculator-results-title"
        aria-live="polite"
      >
        <h2 id="calculator-results-title" class="sr-only">Results</h2>
        <Transition name="fade-up">
          <Card v-if="hasCalculated && result" accent="accent" class="calculator__result-card">
            <div class="calculator__result-header">
              <Icon name="check" :size="22" />
              <span>Your result is ready</span>
            </div>
            <dl class="calculator__cards">
              <ResultCard
                v-for="section in resultCards"
                :key="section.id"
                :label="section.label"
                :value="section.value(result)"
                :format="section.format"
                highlighted
              />
            </dl>
            <ResultTable
              v-if="resultRows.length > 0"
              class="calculator__table"
              :rows="resultRows"
            />
            <div class="calculator__share">
              <Button variant="secondary" size="sm" @click="onShare">
                <Icon name="share" :size="16" />
                Share result
              </Button>
              <Button variant="secondary" size="sm" @click="onEmail">
                <Icon name="mail" :size="16" />
                Email result
              </Button>
            </div>
          </Card>
          <Card v-else class="calculator__placeholder">
            <div class="calculator__placeholder-inner">
              <div class="calculator__placeholder-icon">
                <Icon name="calculator" :size="32" />
              </div>
              <p>Enter your details and press <strong>Calculate</strong> to see your result.</p>
            </div>
          </Card>
        </Transition>
      </section>
    </div>

    <CalculatorContent :definition="definition" />

    <Alert variant="warning" class="calculator__disclaimer" title="Important">
      <p>
        This calculator provides an estimate only and does not constitute financial, tax, legal or
        lending advice. Always verify important figures with a qualified professional or the
        relevant institution.
      </p>
    </Alert>

    <AssumptionsPanel :assumptions="definition.assumptions" />
    <RelatedCalculators :related="definition.related" />
  </PageLayout>
</template>

<style scoped>
.calculator {
  display: grid;
  gap: var(--space-6);
  align-items: start;
}

@media (min-width: 900px) {
  .calculator {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-8);
  }
}

.calculator__form-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-5);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--color-border);
  color: var(--color-primary);
  font-weight: var(--font-semibold);
}

.calculator__fields {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.calculator__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-top: var(--space-2);
}

.calculator__results {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.calculator__result-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.calculator__result-header {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-full);
  background-color: var(--color-success-bg);
  color: var(--color-success);
  font-weight: var(--font-semibold);
  font-size: var(--text-sm);
  width: fit-content;
}

.calculator__cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-4);
  margin: 0;
}

.calculator__table {
  margin-top: var(--space-2);
}

.calculator__share {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.calculator__placeholder {
  color: var(--color-text-muted);
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 50% 30%, rgb(10 92 86 / 0.05), transparent 50%), var(--color-surface);
}

.calculator__placeholder-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
  text-align: center;
}

.calculator__placeholder-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, rgb(10 92 86 / 0.1), rgb(13 148 136 / 0.06));
  color: var(--color-primary);
}

.calculator__placeholder p {
  margin: 0;
  max-width: 28ch;
}

.calculator__disclaimer {
  margin-top: var(--space-8);
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
